import { ChangeDetectorRef, Component, ElementRef, Inject, NgZone, OnInit, ViewChild } from '@angular/core';
import { MAPS_HOST_ADAPTER, MapsHostAdapter, MapsView } from '../../../host-adapter';
import { PartnerLinksService } from '../../../services/partner-links.service';
import { NotifyError, NotifySuccess } from '../../../services/notify';
import type { MapsPartnerCompany, MapsPartnerInvite, MapsPartnerLinkRole } from '../../../models/maps.types';

/**
 * Partner Links admin screen (partner-account model): manage the companies
 * that ARE the partner accounts and the magic-link credentials that admit
 * their recruiters/admins. Replaces the retired CLI issuance script.
 *
 * The raw link URL is shown exactly once, inside the create dialog — the
 * server persists only the token hash, so closing the dialog is final.
 * Dialogs are native <dialog>/showModal(): top-layer modality, Escape close,
 * browser-managed focus restore.
 */
@Component({
    selector: 'maps-admin-partners',
    standalone: false,
    templateUrl: './admin-partners.component.html',
    styleUrl: './admin-partners.component.scss',
})
export class AdminPartnersComponent implements OnInit {
    companies: MapsPartnerCompany[] = [];
    isLoading = true;
    errorMessage: string | null = null;

    // -- new-company form ----------------------------------------------------
    newCompanyName = '';
    isCreatingCompany = false;

    // -- create-link dialog --------------------------------------------------
    @ViewChild('createDialog') createDialog?: ElementRef<HTMLDialogElement>;
    createFor: MapsPartnerCompany | null = null;
    createRole: MapsPartnerLinkRole = 'RECRUITER';
    createName = '';
    /** Optional recruiter email — becomes the automatic Reply-To for contact
     *  sends from sessions this link mints. */
    createEmail = '';
    createYears = 5;
    isCreatingLink = false;
    /** Set once the mutation returns; flips the dialog to its show-once view. */
    createdLinkUrl: string | null = null;
    createdLinkRole: string | null = null;

    // -- revoke dialog -------------------------------------------------------
    @ViewChild('revokeDialog') revokeDialog?: ElementRef<HTMLDialogElement>;
    revokeTarget: { company: MapsPartnerCompany; invite: MapsPartnerInvite } | null = null;
    isRevoking = false;

    // -- hide dialog (remove a revoked link from the list; never a DB delete) -
    @ViewChild('hideDialog') hideDialog?: ElementRef<HTMLDialogElement>;
    hideTarget: { company: MapsPartnerCompany; invite: MapsPartnerInvite } | null = null;
    isHiding = false;

    // -- rotate dialog (confirm view, then the successor's show-once view) ---
    @ViewChild('rotateDialog') rotateDialog?: ElementRef<HTMLDialogElement>;
    rotateTarget: { company: MapsPartnerCompany; invite: MapsPartnerInvite } | null = null;
    isRotating = false;
    /** Set once rotation succeeds; flips the dialog to the show-once view. */
    rotatedLinkUrl: string | null = null;
    rotatedLinkName: string | null = null;

    // -- inline rename -------------------------------------------------------
    renamingInviteId: string | null = null;
    renameDraft = '';
    isRenaming = false;

    constructor(
        private readonly partnerLinks: PartnerLinksService,
        @Inject(MAPS_HOST_ADAPTER) private readonly hostAdapter: MapsHostAdapter,
        private readonly zone: NgZone,
        private readonly cdr: ChangeDetectorRef,
    ) {}

    GoTo(view: MapsView): void {
        this.hostAdapter.NavigateTo(view);
    }

    IsActive(view: MapsView): boolean {
        return this.hostAdapter.CurrentLocation === view;
    }

    async ngOnInit(): Promise<void> {
        await this.reload();
    }

    private async reload(): Promise<void> {
        try {
            const companies = await this.partnerLinks.Companies();
            this.zone.run(() => {
                this.companies = companies;
                this.isLoading = false;
                this.errorMessage = null;
                this.cdr.detectChanges();
            });
        } catch (err) {
            const detail = extractErrorDetail(err);
            console.error('[maps.partners] load failed', err);
            this.zone.run(() => {
                this.errorMessage = `Could not load partner companies: ${detail}`;
                this.isLoading = false;
                this.cdr.detectChanges();
            });
        }
    }

    ActiveInvites(company: MapsPartnerCompany): MapsPartnerInvite[] {
        return company.invites.filter((i) => i.status === 'Active');
    }

    // -- new company ---------------------------------------------------------

    async CreateCompany(): Promise<void> {
        const name = this.newCompanyName.trim();
        if (name.length === 0 || this.isCreatingCompany) return;
        this.isCreatingCompany = true;
        try {
            await this.partnerLinks.CreateCompany(name);
            NotifySuccess(`Company “${name}” created`);
            // Inside the zone + explicit CD: mutating a two-way-bound field from
            // an Apollo continuation mid-pass trips NG0100 in dev mode.
            this.zone.run(() => {
                this.newCompanyName = '';
                this.cdr.detectChanges();
            });
            await this.reload();
        } catch (err) {
            console.error('[maps.partners] company create failed', err);
            NotifyError(`Could not create company: ${extractErrorDetail(err)}`);
        } finally {
            this.zone.run(() => {
                this.isCreatingCompany = false;
                this.cdr.detectChanges();
            });
        }
    }

    // -- create link ---------------------------------------------------------

    OpenCreateLink(company: MapsPartnerCompany): void {
        this.createFor = company;
        this.createRole = 'RECRUITER';
        this.createName = '';
        this.createEmail = '';
        this.createYears = 5;
        this.createdLinkUrl = null;
        this.createdLinkRole = null;
        this.createDialog?.nativeElement.showModal();
    }

    async CreateLink(): Promise<void> {
        if (!this.createFor || this.isCreatingLink || this.createName.trim().length === 0) return;
        this.isCreatingLink = true;
        try {
            const link = await this.partnerLinks.CreateLink({
                companyId: this.createFor.companyId,
                role: this.createRole,
                name: this.createName.trim(),
                email: this.createEmail.trim() || null,
                expiresYears: this.createYears,
            });
            this.zone.run(() => {
                // The one and only surfacing of the raw token (server keeps the hash).
                this.createdLinkUrl = `${this.hostAdapter.ApiOrigin}/magic-link/redeem?token=${link.rawToken}`;
                this.createdLinkRole = link.roleName;
                this.cdr.detectChanges();
            });
            await this.reload();
        } catch (err) {
            console.error('[maps.partners] link create failed', err);
            NotifyError(`Could not create link: ${extractErrorDetail(err)}`);
        } finally {
            this.zone.run(() => {
                this.isCreatingLink = false;
                this.cdr.detectChanges();
            });
        }
    }

    async CopyCreatedLink(): Promise<void> {
        if (!this.createdLinkUrl) return;
        try {
            await navigator.clipboard.writeText(this.createdLinkUrl);
            NotifySuccess('Link copied to clipboard');
        } catch {
            // Clipboard API can be denied — the URL stays selectable in the dialog.
            NotifyError('Copy failed — select the link text and copy manually');
        }
    }

    CloseCreateDialog(): void {
        this.createDialog?.nativeElement.close();
        // Deferred: close() re-enters synchronously via the dialog's (close)
        // event during the same CD pass — resetting bound state inline trips
        // NG0100. A microtask lands the reset after the pass completes.
        queueMicrotask(() =>
            this.zone.run(() => {
                this.createFor = null;
                this.createdLinkUrl = null;
                this.createdLinkRole = null;
                this.cdr.detectChanges();
            })
        );
    }

    // -- inline rename -------------------------------------------------------

    StartRename(invite: MapsPartnerInvite): void {
        this.renamingInviteId = invite.inviteId;
        this.renameDraft = invite.label ?? '';
    }

    CancelRename(): void {
        this.renamingInviteId = null;
        this.renameDraft = '';
    }

    async SaveRename(): Promise<void> {
        const inviteId = this.renamingInviteId;
        const name = this.renameDraft.trim();
        if (!inviteId || this.isRenaming) return;
        if (name.length === 0) {
            this.CancelRename();
            return;
        }
        this.isRenaming = true;
        try {
            await this.partnerLinks.RenameLink(inviteId, name);
            NotifySuccess('Link renamed');
            this.zone.run(() => {
                this.renamingInviteId = null;
                this.renameDraft = '';
                this.cdr.detectChanges();
            });
            await this.reload();
        } catch (err) {
            console.error('[maps.partners] rename failed', err);
            NotifyError(`Could not rename link: ${extractErrorDetail(err)}`);
        } finally {
            this.zone.run(() => {
                this.isRenaming = false;
                this.cdr.detectChanges();
            });
        }
    }

    // -- rotate --------------------------------------------------------------

    OpenRotate(company: MapsPartnerCompany, invite: MapsPartnerInvite): void {
        this.rotateTarget = { company, invite };
        this.rotatedLinkUrl = null;
        this.rotatedLinkName = null;
        this.rotateDialog?.nativeElement.showModal();
    }

    async ConfirmRotate(): Promise<void> {
        if (!this.rotateTarget || this.isRotating) return;
        this.isRotating = true;
        try {
            const link = await this.partnerLinks.RotateLink(this.rotateTarget.invite.inviteId);
            this.zone.run(() => {
                // The successor's ONE surfacing of its raw token.
                this.rotatedLinkUrl = `${this.hostAdapter.ApiOrigin}/magic-link/redeem?token=${link.rawToken}`;
                this.rotatedLinkName = link.name;
                this.cdr.detectChanges();
            });
            await this.reload();
        } catch (err) {
            console.error('[maps.partners] rotate failed', err);
            NotifyError(`Could not rotate link: ${extractErrorDetail(err)}`);
        } finally {
            this.zone.run(() => {
                this.isRotating = false;
                this.cdr.detectChanges();
            });
        }
    }

    async CopyRotatedLink(): Promise<void> {
        if (!this.rotatedLinkUrl) return;
        try {
            await navigator.clipboard.writeText(this.rotatedLinkUrl);
            NotifySuccess('Link copied to clipboard');
        } catch {
            NotifyError('Copy failed — select the link text and copy manually');
        }
    }

    CloseRotateDialog(): void {
        this.rotateDialog?.nativeElement.close();
        // Same NG0100 deferral as CloseCreateDialog.
        queueMicrotask(() =>
            this.zone.run(() => {
                this.rotateTarget = null;
                this.rotatedLinkUrl = null;
                this.rotatedLinkName = null;
                this.cdr.detectChanges();
            })
        );
    }

    // -- revoke --------------------------------------------------------------

    OpenRevoke(company: MapsPartnerCompany, invite: MapsPartnerInvite): void {
        this.revokeTarget = { company, invite };
        this.revokeDialog?.nativeElement.showModal();
    }

    async ConfirmRevoke(): Promise<void> {
        if (!this.revokeTarget || this.isRevoking) return;
        this.isRevoking = true;
        try {
            await this.partnerLinks.RevokeLink(this.revokeTarget.invite.inviteId);
            NotifySuccess('Link revoked — its sessions end on their next request');
            this.CloseRevokeDialog();
            await this.reload();
        } catch (err) {
            console.error('[maps.partners] revoke failed', err);
            NotifyError(`Could not revoke link: ${extractErrorDetail(err)}`);
        } finally {
            this.zone.run(() => {
                this.isRevoking = false;
                this.cdr.detectChanges();
            });
        }
    }

    CloseRevokeDialog(): void {
        this.revokeDialog?.nativeElement.close();
        // Same NG0100 deferral as CloseCreateDialog.
        queueMicrotask(() =>
            this.zone.run(() => {
                this.revokeTarget = null;
                this.cdr.detectChanges();
            })
        );
    }

    // -- hide (remove a revoked link from the list; nothing is deleted) -------

    OpenHide(company: MapsPartnerCompany, invite: MapsPartnerInvite): void {
        this.hideTarget = { company, invite };
        this.hideDialog?.nativeElement.showModal();
    }

    async ConfirmHide(): Promise<void> {
        if (!this.hideTarget || this.isHiding) return;
        this.isHiding = true;
        try {
            await this.partnerLinks.HideLink(this.hideTarget.invite.inviteId);
            NotifySuccess('Link removed from the list');
            this.CloseHideDialog();
            await this.reload();
        } catch (err) {
            console.error('[maps.partners] hide failed', err);
            NotifyError(`Could not remove link: ${extractErrorDetail(err)}`);
        } finally {
            this.zone.run(() => {
                this.isHiding = false;
                this.cdr.detectChanges();
            });
        }
    }

    CloseHideDialog(): void {
        this.hideDialog?.nativeElement.close();
        // Same NG0100 deferral as CloseCreateDialog.
        queueMicrotask(() =>
            this.zone.run(() => {
                this.hideTarget = null;
                this.cdr.detectChanges();
            })
        );
    }
}

function extractErrorDetail(err: unknown): string {
    return (
        (err as { message?: string })?.message ??
        (err as { graphQLErrors?: { message?: string }[] })?.graphQLErrors?.[0]?.message ??
        'unknown error'
    );
}
